//------- import libraries
import { connect } from "react-redux";
import { Col, Modal, Row } from "react-bootstrap";
import React, { useMemo, useRef, useState } from "react";

//------- import custom reuseable components
import { Button, SelectDropDown } from "../../../../shared/components";
import ConfirmationModal from "../../../Inventory/Items/ConfirmationModal/ConfirmationModal";
import SelectedCustomerCard from "../../../../shared/components/customers/SelectedCustomerCard";
import CustomerDropdown from "../../../../shared/components/selectDropdown/CustomerDropdown";
import AddNewCustomerModal from "../../NewTransaction/AddNewCustomer/AddNewCustomerModal";

//------- import constants
import {
  buttonNameConstants,
  buttonTypeConstants,
  EMPTY_STRING,
  shopifyConstants,
} from "../../../../constants";

//------- import styles
import { viewCartPrintDropdownStyle } from "../../../../assets/scss/style/viewCartPrintDropdownStyle";

//------- import images
import collectPosLogoSvg from "../../../../assets/images/collectPosLogo.svg";

//------- import actions
import { shopifyActions, transactionActions } from "../../../../redux/actions";

//------- import utilities
import {
  handleAddNewCustomerClickInTransactionModal,
  handleCustomerEditClickInTransactionModal,
} from "../../../../shared/utilities/customersUtility";
import {
  getReceiptPrintDate,
  getShopifyOrderFulfillmentStatus,
  getShopifyOrderFulfillmentStatusOptions,
  getTotalLineProductsAndQuantity,
  handlePrintShopifyPackingSlips,
  hasItemsFromOtherLocations,
  parseToThousandSeperatorDecimalNumber,
} from "../../../../shared/utility";

const OrderDetailsModal = ({
  orderDetail,
  defaultGridState,
  isShowOrderDetail,
  handleMarkOrderAsOpen,
  toggleOrderDetailModal,
  handleMarkOrderAsReady,
  updateShopifyOrderStatus,
  handleMarkOrderAsFulfilled,
  toggleAddCustomerModal,
  isAddCustomerModal,
  allCustomers,
  currentStore,
  shopifyOrders,
  updateShopifyOrderCustomer,
  shopifyOrdersCallBackConfig,
  handleTransferToLocation,
}) => {
  //------- refs
  const onConfirmActionRef = useRef(null);

  //------- usestates
  const [customerToEdit, setCustomerToEdit] = useState({});
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);

  //------- helper functions
  const toggleConfirmationModal = () => {
    setIsConfirmationModal((prev) => !prev);
  };

  const handleStatusChangeWithConfirmation = (statusActionFn) => {
    const currentStoreLocationId =
      currentStore?.shopify?.location?.shopifyLocationId;
    if (!hasItemsFromOtherLocations(orderDetail, currentStoreLocationId)) {
      statusActionFn();
      return;
    }

    onConfirmActionRef.current = statusActionFn;
    toggleConfirmationModal();
  };

  const handleRemoveCustomerFromOrder = () => {
    updateShopifyOrderCustomer(
      currentStore?.id,
      {
        id: orderDetail?.id,
        customer: {},
      },
      shopifyOrdersCallBackConfig
    );
  };

  const handleUpdateCustomerInOrder = (selectedCustomer) => {
    if (selectedCustomer.value) {
      const customerValue = JSON.parse(selectedCustomer.value);
      updateShopifyOrderCustomer(
        currentStore?.id,
        {
          id: orderDetail?.id,
          customer: {
            ...customerValue,
          },
        },
        shopifyOrdersCallBackConfig
      );
    }
  };

  const isItemCancelInCurrentNotification = (product) => {
    return orderDetail?.refundedItemIds?.includes(product.variant_id)
      ? "text-danger opacity-100"
      : "";
  };

  const currentStoreLocationId =
    currentStore?.shopify?.location?.shopifyLocationId;

  // Group line items by location, splitting quantities if line item is split across locations
  const locationGroups = useMemo(() => {
    if (!Array.isArray(orderDetail?.line_items)) return [];

    const groupsMap = new Map();

    for (const product of orderDetail.line_items) {
      let assigned = [];
      //------- line item saved before location support or without fulfillment location has no location
      if (product.assignedLocations?.length) {
        assigned = product.assignedLocations;
      } else {
        assigned = [
          {
            shopifyLocationId: EMPTY_STRING,
            shopifyLocationName: shopifyConstants.UNASSIGNED_LOCATION,
            quantity: product.current_quantity,
          },
        ];
      }

      for (const loc of assigned) {
        const locId = loc.shopifyLocationId;
        const isCurrent = currentStoreLocationId === locId;
        const locName = loc.shopifyLocationName;
        const key = locId;

        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            locationName: locName,
            locationId: locId,
            isCurrentLocation: isCurrent,
            items: [],
          });
        }
        groupsMap.get(key).items.push({
          ...product,
          displayQuantity: loc.quantity,
          isCurrentLocation: isCurrent,
        });
      }
    }

    return Array.from(groupsMap.values()).sort(
      (a, b) => (b.isCurrentLocation ? 1 : 0) - (a.isCurrentLocation ? 1 : 0)
    );
  }, [orderDetail, currentStoreLocationId]);

  const hasOnlyCurrentLocationLinkedItems =
    locationGroups.length === 1 &&
    locationGroups[0]?.isCurrentLocation &&
    Array.isArray(orderDetail?.line_items) &&
    orderDetail.line_items.length > 0 &&
    orderDetail.line_items.every((item) => item.isCollectPosLinked);

  const showSubtitleLegend =
    (locationGroups.length > 1 &&
      locationGroups.some((group) => group.isCurrentLocation)) ||
    hasOnlyCurrentLocationLinkedItems;

  return (
    <Modal
      show={isShowOrderDetail}
      onHide={toggleOrderDetailModal}
      size="md"
      animation={true}
      centered
      backdrop="static"
      className="add-inventory-modal"
    >
      <Modal.Header closeButton>
        <div className="w-100">
          <Modal.Title className="d-flex align-items-center justify-content-between w-100">
            <span className="modal-sale-transaction-id">
              {`Shopify Order ${orderDetail?.name} (${orderDetail.orderType})`}
            </span>
            <div className="d-flex justify-content-center align-items-center gap-1">
              {getShopifyOrderFulfillmentStatus(
                orderDetail?.orderFulfillmentStatus
              )}
              {getShopifyOrderFulfillmentStatus(orderDetail?.orderPaymentStatus)}
            </div>
          </Modal.Title>
          {showSubtitleLegend && (
            <div className="shopify-location-legend">
              {shopifyConstants.CURRENT_LOCATION_LEGEND}
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body className="pt-0">
        <Row className="m-0 p-0">
          <Col md={12} className="p-0 mb-2">
            <span className="trans-items-modal view-cart-date">
              {getReceiptPrintDate(orderDetail?.createdOn)}
            </span>
          </Col>
          <Col md={12} className="p-0 mb-4 mt-2">
            {orderDetail?.customer?.id ? (
              <SelectedCustomerCard
                customer={{
                  id: orderDetail?.customer?.id,
                  firstName: orderDetail?.customer?.first_name,
                  lastName: orderDetail?.customer?.last_name,
                  mobile: orderDetail?.customer?.phone,
                  email: orderDetail?.customer?.email,
                }}
                isDeleteEditOptions={true}
                isStoreCredit={false}
                handleCustomerDeleteClick={handleRemoveCustomerFromOrder}
                handleCustomerEditClick={() => {
                  handleCustomerEditClickInTransactionModal(
                    allCustomers,
                    setCustomerToEdit,
                    toggleAddCustomerModal,
                    orderDetail
                  );
                }}
                className={{
                  parnetDiv: "selected-customer-wrapper gap-3",
                  nameDiv: "selected-customer-wrapper",
                  nameSpan: "trans-product-name-modal-total",
                  editDeleteDiv: "ms-auto",
                  deleteButton: "p-0 opacity-50",
                  editButton: "p-0 opacity-50",
                }}
              />
            ) : (
              <CustomerDropdown
                value={""}
                menuPortalTarget=""
                dropdownIndicator={false}
                handleAddCustomerClick={() =>
                  handleAddNewCustomerClickInTransactionModal(
                    setCustomerToEdit,
                    toggleAddCustomerModal
                  )
                }
                handleChange={handleUpdateCustomerInOrder}
              />
            )}
          </Col>
          <AddNewCustomerModal
            addCustomerModal={isAddCustomerModal}
            customerToEdit={customerToEdit}
            shopifyOrdersCallBackConfig={shopifyOrdersCallBackConfig}
          />
        </Row>
        <Row className="m-0 p-0 ">
          <div className="container-scrollbar p-0  trans-modal-card-wrapper ">
            <Row className="m-0">
              <Col
                sm={9}
                className="px-0 d-flex align-items-center mb-3 history-cart-table-header"
              >
                Product Name
              </Col>
              <Col
                sm={3}
                className="px-0 d-flex align-items-center justify-content-end mb-3 pe-1 history-cart-table-header"
              >
                {"Price"}
              </Col>
              {locationGroups.map((group, groupIdx) => (
                <React.Fragment key={groupIdx}>
                  {locationGroups.length > 1 && (
                    <Col md={12} className="px-0 py-1 mt-2 mb-2 border-bottom">
                      <span
                        className={`fw-bold shopify-location-header ${
                          group.isCurrentLocation
                            ? "current-location"
                            : EMPTY_STRING
                        }`}
                      >
                        {group.locationName}
                      </span>
                    </Col>
                  )}
                  {group.items.map((product, pIdx) => {
                    const currentLocationClass = group.isCurrentLocation
                      ? "shopify-current-location-text"
                      : EMPTY_STRING;
                    return (
                      <React.Fragment key={pIdx}>
                        <Col sm={9} className="px-0 d-flex align-items-center">
                          <div className="d-flex align-items-center gap-1">
                            <span
                              className={`trans-items-modal ${currentLocationClass} ${isItemCancelInCurrentNotification(
                                product
                              )}`}
                            >
                              {product.displayQuantity}
                            </span>
                            {product?.isCollectPosLinked && (
                              <span
                                title="Managed through CollectPOS"
                                className="d-inline-flex align-items-center"
                              >
                                <img
                                  src={collectPosLogoSvg}
                                  alt="CollectPOS"
                                  className="shopify-collectpos-logo"
                                />
                              </span>
                            )}
                            <span
                              className={`trans-product-name-modal ${
                                product?.isPartiallyCancel
                                  ? "opacity-50"
                                  : EMPTY_STRING
                              } ${currentLocationClass} ${isItemCancelInCurrentNotification(
                                product
                              )}`}
                            >
                              {product.title}
                            </span>
                          </div>
                        </Col>
                        <Col
                          sm={3}
                          className="px-0 d-flex align-items-center justify-content-end"
                        >
                          <div>
                            <span
                              className={`trans-product-name-modal ${currentLocationClass} ${isItemCancelInCurrentNotification(
                                product
                              )}`}
                            >
                              $
                              {parseToThousandSeperatorDecimalNumber(
                                product?.price
                              )}
                            </span>
                          </div>
                        </Col>
                        <Col
                          md={12}
                          className={`m-0 p-0 d-flex justify-content-between mb-2`}
                        >
                          <div className="d-flex align-items-center gap-1">
                            <span
                              className={`trans-items-modal ms-3 ${isItemCancelInCurrentNotification(
                                product
                              )}`}
                            >
                              {product?.vendor}
                            </span>{" "}
                            <span
                              className={`trans-items-modal ${isItemCancelInCurrentNotification(
                                product
                              )}`}
                            >
                              {product?.variant_title
                                ? `(${product?.variant_title})`
                                : ""}
                            </span>
                            {product?.serialNumbers?.length ? (
                              <span
                                className={`trans-items-modal ms-3 ${isItemCancelInCurrentNotification(
                                  product
                                )}`}
                              >
                                Serial Number(s) :{" "}
                                {product?.serialNumbers?.join(", ")}
                              </span>
                            ) : (
                              ""
                            )}
                            {product.lineItemStatus && (
                              <span className="text-danger">
                                ({product.lineItemStatus})
                              </span>
                            )}
                            {product.isItemOnHold ? (
                              <span className="on-hold-item">(Hold)</span>
                            ) : (
                              ""
                            )}
                          </div>
                        </Col>
                        <Col
                          md={12}
                          className={`m-0 p-0 d-flex justify-content-between mb-2`}
                        >
                          <div className="d-flex align-items-center gap-1">
                            <span
                              className={`trans-items-modal ms-3 ${isItemCancelInCurrentNotification(
                                product
                              )}`}
                            >
                              {product?.isOnline
                                ? "(Online)"
                                : "(In-Store/Online)"}
                            </span>
                          </div>
                        </Col>
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </Row>
          </div>
          <Col
            md={12}
            className="m-0 mt-2 mb-2 p-0 d-flex justify-content-between tran-modal-detail-margin"
          >
            <div className="trans-product-name-modal-text m-0">Subtotal</div>

            <div className="">
              {` $${parseToThousandSeperatorDecimalNumber(
                orderDetail?.total_line_items_price
              )}`}
            </div>
          </Col>
          <>
            <Col
              md={12}
              className="m-0  mb-2 p-0 d-flex justify-content-between tran-modal-detail-margin"
            >
              <div className="trans-product-name-modal-text m-0">Discount</div>
              <div className="">
                {`$${parseToThousandSeperatorDecimalNumber(
                  orderDetail?.current_total_discounts
                )}`}
              </div>
            </Col>

            <Col
              md={12}
              className="m-0  mb-2 p-0 d-flex justify-content-between tran-modal-detail-margin"
            >
              <div className="trans-product-name-modal-text m-0">{`Tax`}</div>
              <div className="">
                {`$${parseToThousandSeperatorDecimalNumber(
                  orderDetail?.current_total_tax ?? 0
                )}`}
              </div>
            </Col>
            {orderDetail?.shipping_address && (
              <Col
                md={12}
                className="m-0  mb-2 p-0 d-flex justify-content-between tran-modal-detail-margin"
              >
                <div className="trans-product-name-modal-text m-0">{`Shipping Charges`}</div>
                <div className="">
                  {`$${parseToThousandSeperatorDecimalNumber(
                    orderDetail?.current_shipping_price_set?.shop_money
                      ?.amount ?? 0
                  )}`}
                </div>
              </Col>
            )}
          </>

          <Col
            md={12}
            className="m-0  mb-2 p-0 pt-2 d-flex justify-content-between border-top tran-modal-detail-margin"
          >
            <div className="trans-product-name-modal-total m-0">
              TOTAL (
              {getTotalLineProductsAndQuantity(orderDetail)?.totalQuantity}{" "}
              items)
            </div>

            <div className="trans-product-name-modal-total">
              $
              {parseToThousandSeperatorDecimalNumber(
                orderDetail?.current_total_price
              )}
            </div>
          </Col>
        </Row>
        {orderDetail?.note && (
          <Col md={12} className="m-0 mt-3   p-0">
            <div className="note-text-div container-scrollbar">
              <p className="trans-items-modal mb-2">Note</p>
              <span className="note-text">{orderDetail?.note}</span>
            </div>
          </Col>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-center gap-2 w-100">
          <Button
            type="button"
            label={buttonNameConstants.CLOSE}
            handleClick={() => toggleOrderDetailModal()}
            buttonType={buttonTypeConstants.GHOST_BUTTON}
            className="w-100"
          />
          <Button
            type="button"
            className="w-100"
            buttonType={buttonTypeConstants.LIGHT_PURPLE}
            label={buttonNameConstants.PRINT}
            handleClick={() =>
              handlePrintShopifyPackingSlips([orderDetail], currentStore)
            }
          ></Button>
          {currentStore.shopify && (
            <SelectDropDown
              outerWrapperClassname="w-100"
              menuPortalTarget=""
              noOptionsMessage="No Status Found"
              isSingleValue="Set Status"
              placeHolder="Set Status"
              handleChange={(option) => {
                if (option?.value) {
                  if (option.value === buttonNameConstants.MARK_AS_READY) {
                    handleStatusChangeWithConfirmation(() =>
                      handleMarkOrderAsReady(
                        updateShopifyOrderStatus,
                        currentStore,
                        defaultGridState,
                        orderDetail,
                        toggleOrderDetailModal
                      )
                    );
                  } else if (
                    option.value === buttonNameConstants.MARK_AS_OPEN
                  ) {
                    handleStatusChangeWithConfirmation(() =>
                      handleMarkOrderAsOpen(
                        updateShopifyOrderStatus,
                        currentStore,
                        defaultGridState,
                        orderDetail,
                        toggleOrderDetailModal
                      )
                    );
                  } else if (
                    option.value === buttonNameConstants.MARK_AS_FULFILLED
                  ) {
                    handleStatusChangeWithConfirmation(() =>
                      handleMarkOrderAsFulfilled(
                        updateShopifyOrderStatus,
                        currentStore,
                        defaultGridState,
                        orderDetail,
                        toggleOrderDetailModal
                      )
                    );
                  } else if (
                    option.value === buttonNameConstants.TRANSFER_TO_LOCATION
                  ) {
                    handleStatusChangeWithConfirmation(() =>
                      handleTransferToLocation(
                        updateShopifyOrderStatus,
                        currentStore,
                        defaultGridState,
                        orderDetail,
                        toggleOrderDetailModal
                      )
                    );
                  }
                }
              }}
              styles={viewCartPrintDropdownStyle}
              menuPlacement="bottom"
              options={getShopifyOrderFulfillmentStatusOptions(orderDetail)}
            />
          )}
        </div>
      </Modal.Footer>
      <ConfirmationModal
        isConfirmationModal={isConfirmationModal}
        title={shopifyConstants.LOCATION_CONFIRMATION.TITLE}
        message={shopifyConstants.LOCATION_CONFIRMATION.SINGLE_ORDER_MESSAGE}
        toggleConfirmationModal={toggleConfirmationModal}
        handleNoButtonClick={toggleConfirmationModal}
        handleYesButtonClick={() => {
          toggleConfirmationModal();
          onConfirmActionRef.current();
        }}
      />
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  allCustomers: state.customer.allCustomers,
  shopifyOrders: state.shopify.shopifyOrders,
  isAddCustomerModal: state.transaction.isAddCustomerModal,
});

const mapDispatchToProps = (dispatch) => ({
  toggleAddCustomerModal: (value) =>
    dispatch(transactionActions.toggleAddCustomerModal(value)),
  updateShopifyOrderCustomer: (storeId, data, shopifyOrdersCallBackConfig) =>
    dispatch(
      shopifyActions.updateShopifyOrderCustomer(
        storeId,
        data,
        shopifyOrdersCallBackConfig
      )
    ),
});

//-------Export History Component
export default connect(mapStateToProps, mapDispatchToProps)(OrderDetailsModal);
